// File editor for an uploaded bundle: list, open, save, delete and add files
// through /{kind}/{id}/files (the same API the landing and offer editors use).
// kind = pre-landings | landing-pages | offers; item = the row or null (closed).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { FileCode, FileText, FolderOpen, Save, Trash2 } from 'lucide-react';

const filePart = (p) => String(p || '').split('/').map(encodeURIComponent).join('/');

export default function BundleFileEditor({ kind, item, onClose }) {
  const { t } = useT();
  const [files, setFiles] = useState([]);
  const [active, setActive] = useState(null);
  const [content, setContent] = useState('');
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const base = item ? API + '/' + kind + '/' + item.id + '/files' : '';

  const load = useCallback(async () => {
    if (!item) return;
    setLoading(true);
    try {
      const res = await axios.get(API + '/' + kind + '/' + item.id + '/files');
      setFiles(res.data.files || []);
    } catch (err) {
      toast.error(t('Error loading files'));
    } finally {
      setLoading(false);
    }
  }, [kind, item, t]);

  useEffect(() => {
    setActive(null);
    setContent('');
    setDirty(false);
    setFiles([]);
    if (item) load();
  }, [item, load]);

  const close = () => {
    if (dirty && !window.confirm(t('Discard unsaved changes?'))) return;
    if (onClose) onClose();
  };

  const openFile = async (path) => {
    if (dirty && !window.confirm(t('Discard unsaved changes?'))) return;
    try {
      setLoading(true);
      const res = await axios.get(base + '/' + filePart(path));
      setActive(path);
      setContent(res.data.content || '');
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Error loading file'));
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!active || !base) return;
    try {
      setSaving(true);
      await axios.put(base + '/' + filePart(active), { content });
      setDirty(false);
      toast.success(t('File saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Error saving file'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (path) => {
    if (!base || !path) return;
    if (!window.confirm(t('Delete this file?') + ' ' + path)) return;
    try {
      await axios.delete(base + '/' + filePart(path));
      if (active === path) {
        setActive(null);
        setContent('');
        setDirty(false);
      }
      toast.success(t('File deleted'));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Error deleting file'));
    }
  };

  const upload = async (fileObj) => {
    if (!fileObj || !base) return;
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append('file', fileObj);
      await axios.post(base, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('File uploaded'));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Error uploading file'));
    } finally {
      setLoading(false);
    }
  };

  const isEntry = (path) => !!path && path === item?.index_file;

  return (
    <Dialog open={!!item} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="max-w-6xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5" />
            {item?.name} — {t('File editor')}
          </DialogTitle>
          <DialogDescription>{t('Edit HTML, CSS, JS and other text files inside the uploaded archive')}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-4" style={{ height: '65vh' }}>
          <div className="w-64 flex-shrink-0 border rounded-lg overflow-y-auto bg-muted/30">
            <div className="p-2 text-xs font-medium text-muted-foreground uppercase tracking-wide border-b flex items-center justify-between">
              <span>{t('Files')}</span>
              <label className="cursor-pointer text-primary hover:underline font-normal normal-case tracking-normal">
                {t('+ Upload')}
                <input type="file" className="hidden" onChange={(e) => { const fobj = e.target.files && e.target.files[0]; e.target.value = ''; upload(fobj); }} />
              </label>
            </div>
            {loading && !active ? (
              <div className="p-4 text-sm text-muted-foreground">{t('Loading...')}</div>
            ) : (
              <div className="p-1">
                {files.map((f) => (
                  <button
                    key={f.path}
                    type="button"
                    onClick={() => f.editable && openFile(f.path)}
                    className={`w-full text-left px-3 py-1.5 rounded text-sm flex items-center gap-2 transition-colors ${
                      active === f.path ? 'bg-primary/10 text-primary font-medium' : f.editable ? 'hover:bg-muted cursor-pointer' : 'text-muted-foreground cursor-default opacity-60'
                    }`}
                    title={f.editable ? f.path : t('Binary file — not editable')}
                  >
                    <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{f.path}</span>
                    {isEntry(f.path) && (
                      <Badge variant="secondary" className="text-[10px] ml-auto flex-shrink-0">{t('Start page')}</Badge>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex-1 flex flex-col min-w-0">
            {active ? (
              <>
                <div className="flex items-center justify-between pb-2 border-b mb-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
                    <FileCode className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{active}</span>
                    {dirty && <Badge variant="outline" className="text-xs">{t('Not saved')}</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => remove(active)} disabled={isEntry(active)} aria-label={isEntry(active) ? t('Set another entry page first') : t('Delete file')} title={isEntry(active) ? t('Set another entry page first') : t('Delete file')}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Button size="sm" onClick={save} disabled={saving || !dirty}>
                      <Save className="w-4 h-4 mr-1" />
                      {saving ? t('Saving...') : t('Save')}
                    </Button>
                  </div>
                </div>
                <textarea
                  className="flex-1 w-full font-mono text-sm p-3 border rounded-lg resize-none bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  value={content}
                  onChange={(e) => { setContent(e.target.value); setDirty(true); }}
                  spellCheck={false}
                />
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <FileCode className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">{t('Select a file from the sidebar to edit')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
